import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Broadcast, Calendar, CurrencyEur, Ticket, Play } from "@phosphor-icons/react";

const ConcertDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [concert, setConcert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [hasTicket, setHasTicket] = useState(false);
  const [polling, setPolling] = useState(false);

  const fetchConcert = async () => {
    const { data } = await api.get(`/concerts/${id}`);
    setConcert(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchConcert();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    setPolling(true);
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Paiement réussi ! Ticket activé 🎟️");
          setHasTicket(true);
          setPolling(false);
          fetchConcert();
          return;
        }
        if (data.status === "expired") {
          toast.error("Session expirée");
          setPolling(false);
          return;
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.warn("payment poll error", e);
      }
      if (attempts < 8) setTimeout(poll, 2500);
      else setPolling(false);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const buy = async () => {
    if (!user) { nav("/login", { state: { from: `/concert/${id}` } }); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        concert_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur paiement");
      setBuying(false);
    }
  };

  const joinFree = async () => {
    if (!user) { nav("/login", { state: { from: `/concert/${id}` } }); return; }
    setBuying(true);
    try {
      await api.post(`/concerts/${id}/join-free`);
      toast.success("Vous êtes inscrit ! Rendez-vous au concert.");
      setHasTicket(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBuying(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] text-white"><Navbar /><p className="p-10">Chargement...</p></div>;
  if (!concert) return null;

  const when = new Date(concert.scheduled_at);
  const isArtistOwner = user && user.id === concert.artist_id;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="relative h-[60vh] overflow-hidden">
        <img src={concert.cover_image_url || "https://images.pexels.com/photos/30215324/pexels-photo-30215324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
          alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto">
          {concert.is_live && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/90 text-xs font-bold uppercase tracking-wider mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> EN DIRECT
            </div>
          )}
          <p className="text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-3">{concert.genre}</p>
          <h1 data-testid="concert-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-3">{concert.title}</h1>
          <p className="text-xl text-zinc-300">par <strong>{concert.artist_name}</strong></p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="font-display font-bold text-2xl mb-4">À propos</h2>
          <p className="text-zinc-300 leading-relaxed">{concert.description}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-10">
            <div className="p-4 rounded-xl border border-white/10 bg-[#0f0f10]">
              <Calendar size={20} className="text-[#E11D48] mb-2" />
              <div className="text-xs uppercase tracking-widest text-zinc-500">Date</div>
              <div className="font-bold">{when.toLocaleDateString("fr-FR")}</div>
              <div className="text-sm text-zinc-400">{when.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <div className="p-4 rounded-xl border border-white/10 bg-[#0f0f10]">
              <Broadcast size={20} className="text-[#E11D48] mb-2" />
              <div className="text-xs uppercase tracking-widest text-zinc-500">Spectateurs</div>
              <div className="font-bold">{concert.attendees_count}</div>
            </div>
            <div className="p-4 rounded-xl border border-white/10 bg-[#0f0f10]">
              <CurrencyEur size={20} className="text-[#E11D48] mb-2" />
              <div className="text-xs uppercase tracking-widest text-zinc-500">Tarif</div>
              <div className="font-bold">{concert.is_free ? "Gratuit" : `${concert.price_eur}€`}</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-1">
          <div className="rounded-2xl border border-white/10 bg-[#0f0f10] p-6 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Accès au live</div>
            <div className="font-display font-black text-3xl mb-4">{concert.is_free ? "Gratuit" : `${concert.price_eur}€`}</div>
            {polling && <p className="text-sm text-[#F59E0B] mb-3">Vérification du paiement...</p>}

            {hasTicket || isArtistOwner ? (
              <Button data-testid="concert-enter-live" className="w-full bg-[#10B981] hover:bg-[#059669] text-white" asChild>
                <Link to={`/live/${concert.id}`}><Play weight="fill" className="mr-2" /> Entrer dans le live</Link>
              </Button>
            ) : concert.is_free ? (
              <Button data-testid="concert-join-free" disabled={buying} onClick={joinFree}
                className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white">
                <Ticket weight="fill" className="mr-2" /> Rejoindre gratuitement
              </Button>
            ) : (
              <Button data-testid="concert-buy-ticket" disabled={buying} onClick={buy}
                className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white">
                {buying ? "Redirection..." : <><Ticket weight="fill" className="mr-2" /> Acheter mon ticket</>}
              </Button>
            )}

            <div className="mt-6 pt-6 border-t border-white/5 text-xs text-zinc-500 leading-relaxed">
              Paiement sécurisé. Accès instantané au live, au chat et aux replays.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConcertDetail;
